import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import defaultLogo from 'assets/img/logo.svg';

type IndicatorPosition = 'top' | 'center' | 'none';
type SpinPhase = 'idle' | 'spinning' | 'decelerating' | 'finished';

export type RouletteSegment = {
  id: number;
  label: string;
};

export type RouletteWheelRef = {
  startSpin: () => void;
  stopSpin: () => void;
  reset: () => void;
  phase: SpinPhase;
};

type RouletteWheelProps = {
  segments: RouletteSegment[];
  centerImageSrc?: string;
  diameter?: number;
  indicatorPosition?: IndicatorPosition;
  spinSpeed?: number;       // deg/s (default 800)
  decelDuration?: number;   // ms (default 5000)
  onSpinEnd?: (selectedSegment: RouletteSegment) => void;
};

const SEGMENT_COLORS = ['#ffc200', '#FFFFFF'];
const RING_COLOR = '#DA291C';
const CENTER_WHITE = '#FFFFFF';
const CENTER_RED = '#DA291C';
const CENTER_GOLD = '#DA291C';
const TEXT_COLOR = '#27251F';
const BULB_COUNT = 12;

const RouletteWheel = forwardRef<RouletteWheelRef, RouletteWheelProps>(({
  segments,
  centerImageSrc,
  diameter = 500,
  indicatorPosition = 'top',
  spinSpeed = 800,
  decelDuration = 5000,
  onSpinEnd,
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [centerImage, setCenterImage] = useState<HTMLImageElement | null>(null);
  const [rotationDeg, setRotationDeg] = useState(0);
  const [phase, setPhase] = useState<SpinPhase>('idle');

  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const spinStartAngleRef = useRef<number>(0);
  const decelStartTimeRef = useRef<number>(0);

  // refs para tener los valores actuales dentro de los callbacks de animación
  const spinSpeedRef = useRef(spinSpeed);
  const decelDurationRef = useRef(decelDuration);
  const onSpinEndRef = useRef(onSpinEnd);
  const segmentsRef = useRef(segments);

  useEffect(() => { spinSpeedRef.current = spinSpeed; }, [spinSpeed]);
  useEffect(() => { decelDurationRef.current = decelDuration; }, [decelDuration]);
  useEffect(() => { onSpinEndRef.current = onSpinEnd; }, [onSpinEnd]);
  useEffect(() => { segmentsRef.current = segments; }, [segments]);

  // si no viene imagen, carga el logo por defecto
  useEffect(() => {
    const src = centerImageSrc || defaultLogo;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setCenterImage(img);
    img.src = src;
  }, [centerImageSrc]);

  useEffect(() => {
    draw();
  }, [segments, rotationDeg, centerImage, diameter, indicatorPosition]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const animateSpin = useCallback((timestamp: number) => {
    if (!lastTimeRef.current) lastTimeRef.current = timestamp;
    const delta = (timestamp - lastTimeRef.current) / 1000;
    lastTimeRef.current = timestamp;

    setRotationDeg(prev => (prev + spinSpeedRef.current * delta) % 360000);
    rafRef.current = requestAnimationFrame(animateSpin);
  }, []);

  // calcula qué segmento queda bajo el indicador según el ángulo final
  const getSelectedSegment = useCallback((finalRotationDeg: number): RouletteSegment | null => {
    const segs = segmentsRef.current;
    const n = segs.length;
    if (n === 0) return null;

    const sliceDeg = 360 / n;
    // el indicador está arriba (-90°), normalizar al sistema local de la ruleta
    const localAngle = (((-90 - finalRotationDeg) % 360) + 360) % 360;
    const index = Math.floor(localAngle / sliceDeg);
    return segs[index];
  }, []);

  const animateDecel = useCallback((timestamp: number) => {
    if (!decelStartTimeRef.current) decelStartTimeRef.current = timestamp;
    const elapsed = timestamp - decelStartTimeRef.current;
    const dur = decelDurationRef.current;
    const progress = Math.min(elapsed / dur, 1);

    // recorrido calculado para que la velocidad inicial = spinSpeed
    const decelTravel = spinSpeedRef.current * (dur / 1000) / 3;
    const eased = 1 - Math.pow(1 - progress, 3);
    const currentAngle = spinStartAngleRef.current + decelTravel * eased;

    setRotationDeg(currentAngle);

    if (progress < 1) {
      rafRef.current = requestAnimationFrame(animateDecel);
    } else {
      setPhase('finished');
      const selected = getSelectedSegment(currentAngle);
      if (selected) onSpinEndRef.current?.(selected);
    }
  }, [getSelectedSegment]);

  const startSpin = useCallback(() => {
    if (phase === 'spinning' || phase === 'decelerating') return;
    setPhase('spinning');
    lastTimeRef.current = 0;
    rafRef.current = requestAnimationFrame(animateSpin);
  }, [phase, animateSpin]);

  const stopSpin = useCallback(() => {
    if (phase !== 'spinning') return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    setRotationDeg(prev => {
      spinStartAngleRef.current = prev;
      return prev;
    });

    decelStartTimeRef.current = 0;
    setPhase('decelerating');
    rafRef.current = requestAnimationFrame(animateDecel);
  }, [phase, animateDecel]);

  const reset = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setPhase('idle');
    setRotationDeg(0);
  }, []);

  useImperativeHandle(ref, () => ({
    startSpin,
    stopSpin,
    reset,
    phase,
  }), [startSpin, stopSpin, reset, phase]);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // retina support
    const dpr = window.devicePixelRatio || 1;
    canvas.width = diameter * dpr;
    canvas.height = diameter * dpr;
    canvas.style.width = `${diameter}px`;
    canvas.style.height = `${diameter}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, diameter, diameter);

    const cx = diameter / 2;
    const cy = diameter / 2;

    // medidas de la ruleta (de afuera hacia adentro)
    const outerBorder = 3;                 // borde blanco exterior
    const outerRadius = diameter / 2 - 10; // 10px de margen para que quepa el indicador top
    const ringRadius = outerRadius - outerBorder;
    const ringWidth = ringRadius * 0.1;    // grosor del anillo rojo (subir para más ancho)
    const wheelRadius = ringRadius - ringWidth;
    const n = segments.length;

    if (n === 0) {
      ctx.fillStyle = '#e0e0e0';
      ctx.beginPath();
      ctx.arc(cx, cy, outerRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#999';
      ctx.font = '16px Lato, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Sin segmentos', cx, cy);
      return;
    }

    // borde blanco exterior
    ctx.beginPath();
    ctx.arc(cx, cy, outerRadius, 0, Math.PI * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.fill();

    // anillo rojo
    ctx.beginPath();
    ctx.arc(cx, cy, ringRadius, 0, Math.PI * 2);
    ctx.fillStyle = RING_COLOR;
    ctx.fill();

    // bombillos sobre el anillo rojo
    const bulbDist = ringRadius - ringWidth / 2; // punto medio del anillo
    const bulbRadius = ringWidth * 0.3;          // tamaño de cada bombillo

    for (let i = 0; i < BULB_COUNT; i++) {
      const angle = (i / BULB_COUNT) * Math.PI * 2 - Math.PI / 2; // empieza desde arriba
      const bx = cx + bulbDist * Math.cos(angle);
      const by = cy + bulbDist * Math.sin(angle);

      // base
      ctx.beginPath();
      ctx.arc(bx, by, bulbRadius + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#FFFDE7';
      ctx.fill();

      // cuerpo con gradiente (brillo desplazado arriba-izq)
      const grad = ctx.createRadialGradient(
        bx - bulbRadius * 0.25, by - bulbRadius * 0.25, bulbRadius * 0.1,
        bx, by, bulbRadius,
      );
      grad.addColorStop(0, '#FFFDE7');
      grad.addColorStop(0.5, '#faeab9');
      grad.addColorStop(1, '#ffe294');
      ctx.beginPath();
      ctx.arc(bx, by, bulbRadius, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // reflejo blanco (0.35 = tamaño, 0.6 = opacidad)
      ctx.beginPath();
      ctx.arc(bx - bulbRadius * 0.25, by - bulbRadius * 0.25, bulbRadius * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.fill();
    }

    // segmentos (giran con rotationDeg)
    const sliceAngle = (2 * Math.PI) / n;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate((rotationDeg * Math.PI) / 180);

    for (let i = 0; i < n; i++) {
      const startAngle = i * sliceAngle;
      const endAngle = (i + 1) * sliceAngle;
      const color = SEGMENT_COLORS[i % SEGMENT_COLORS.length];

      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, wheelRadius, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();

      // texto del segmento
      ctx.save();
      ctx.rotate(startAngle + sliceAngle / 2);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = TEXT_COLOR;
      const fontSize = Math.max(10, Math.min(14, 200 / n)); // se adapta a la cantidad
      ctx.font = `bold ${fontSize}px Lato, sans-serif`;
      const label =
        segments[i].label.length > 12
          ? segments[i].label.substring(0, 12) + '..'
          : segments[i].label;
      const textStart = wheelRadius - 18;
      const centerSafe = wheelRadius * 0.25; // margen desde el centro
      const maxTextWidth = textStart - centerSafe;
      ctx.fillText(label, textStart, 0, maxTextWidth);
      ctx.restore();
    }

    ctx.restore();

    // círculo central (0.2 = 20% del wheelRadius)
    const centerRadius = wheelRadius * 0.2;

    // borde blanco del centro (+2px)
    ctx.beginPath();
    ctx.arc(cx, cy, centerRadius + 2, 0, Math.PI * 2);
    ctx.fillStyle = CENTER_WHITE;
    ctx.fill();

    // fondo del centro
    ctx.beginPath();
    ctx.arc(cx, cy, centerRadius, 0, Math.PI * 2);
    ctx.fillStyle = CENTER_GOLD;
    ctx.fill();

    // imagen/logo recortado circular (4px de padding)
    if (centerImage) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, centerRadius - 4, 0, Math.PI * 2);
      ctx.clip();
      const imgSize = (centerRadius - 4) * 2;
      ctx.drawImage(centerImage, cx - imgSize / 2, cy - imgSize / 2, imgSize, imgSize);
      ctx.restore();
    }

    // indicador
    if (indicatorPosition === 'top') {
      // triángulo arriba de la ruleta, ancho 28px, alto 22px
      ctx.save();
      ctx.translate(cx, cy - outerRadius - 2);
      ctx.beginPath();
      ctx.moveTo(-14, -18);
      ctx.lineTo(14, -18);
      ctx.lineTo(0, 4);
      ctx.closePath();
      ctx.fillStyle = CENTER_RED;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    } else if (indicatorPosition === 'center') {
      // triángulo desde el centro hacia arriba, ancho 20px, alto 20px
      ctx.save();
      ctx.translate(cx, cy - centerRadius - 4);
      ctx.beginPath();
      ctx.moveTo(-10, 4);
      ctx.lineTo(10, 4);
      ctx.lineTo(0, -16);
      ctx.closePath();
      ctx.fillStyle = CENTER_RED;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.restore();
    }
  };

  return (
    <div className="roulette_wheel_wrapper">
      <canvas ref={canvasRef} />
    </div>
  );
});

RouletteWheel.displayName = 'RouletteWheel';

export default RouletteWheel;
